import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { Sparkles, ChevronRight, Trophy, TrendingDown, Target } from 'lucide-react';
import axios from 'axios';
import { API } from '../config/api';

export const CoachCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [showWhy, setShowWhy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/coach/home-card`, { withCredentials: true })
      .then((res) => setCard(res.data))
      .catch(() => setCard(null));
  }, []);

  if (!card || card.state === 'DISABLED') return null;

  const isMilestone = card.state === 'MILESTONE';
  const headline = isMilestone
    ? (t('coach.milestoneTitle') || 'Nuovo traguardo')
    : card.state === 'PLATEAU' ? (t('coach.plateauTitle') || 'Facciamo il punto?')
    : card.state === 'NEW_USER' ? (t('coach.newUserTitle') || 'Conosciamoci')
    : (t('coach.normalTitle') !== 'coach.normalTitle' && t('coach.normalTitle')) || 'Il tuo check-in di oggi';

  // Tono enterprise: niente emoji ripetute in testa al messaggio
  const message = (card.message || '').replace(/^[\s\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}]+/u, '');

  return (
    <div
      data-testid="coach-home-card"
      className="relative overflow-hidden rounded-2xl mb-4 border border-white/[0.08]"
      style={{ background: 'linear-gradient(160deg, #131C33 0%, #0E1526 55%, #101A30 100%)' }}
    >
      {/* glow decorativo */}
      <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-[0.14]"
           style={{ background: 'radial-gradient(circle, #F43F5E 0%, transparent 70%)' }} />
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-px"
           style={{ background: 'linear-gradient(90deg, transparent, rgba(244,63,94,0.5), transparent)' }} />

      <div className="relative p-5">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#F43F5E]/25"
               style={{ background: 'linear-gradient(135deg, #F43F5E 0%, #E11D48 100%)' }}>
            {isMilestone ? <Trophy className="w-5 h-5 text-white" /> : <Sparkles className="w-5 h-5 text-white" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#F43F5E]/90 leading-none mb-1">
              Slimmy Coach
            </p>
            <h3 className="text-white font-bold text-base leading-tight">{headline}</h3>
          </div>
          {card.progress != null && card.progress < 0 && (
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-400/10 border border-emerald-400/20 text-emerald-400 text-xs font-semibold">
              <TrendingDown className="w-3 h-3" />{card.progress} kg
            </span>
          )}
        </div>

        {message && (
          <p className="text-[13.5px] leading-relaxed text-gray-300/90 mt-3" data-testid="coach-card-message">{message}</p>
        )}

        {card.mission && card.mission.type !== 'none' && (
          <div className="relative mt-4 rounded-xl bg-white/[0.04] border border-white/[0.06] overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#F43F5E] to-[#E11D48]" />
            <div className="pl-4 pr-3 py-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Target className="w-3 h-3 text-[#F43F5E]/80" />
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                  {t('coach.todayMission') || 'Missione di oggi'}
                </p>
                <button
                  data-testid="coach-why-btn"
                  onClick={() => setShowWhy((v) => !v)}
                  className="ml-auto text-[11px] font-medium text-gray-500 hover:text-gray-300 transition-colors underline underline-offset-2 decoration-gray-600"
                >
                  {t('coach.why') || 'Perché?'}
                </button>
              </div>
              <p className="text-sm text-white font-semibold" data-testid="coach-mission-title">{card.mission.title}</p>
              {showWhy && (
                <p className="text-xs text-gray-400 mt-1.5 leading-relaxed" data-testid="coach-mission-why">{card.mission.why}</p>
              )}
            </div>
          </div>
        )}

        <button
          data-testid="coach-talk-btn"
          onClick={() => navigate('/app/coach')}
          className="group w-full mt-4 py-3 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-1.5 shadow-lg shadow-[#F43F5E]/20 transition-transform active:scale-[0.98]"
          style={{ background: 'linear-gradient(135deg, #F43F5E 0%, #E11D48 100%)' }}
        >
          {t('coach.talk') || 'Parla con il Coach'}
          <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
};

export default CoachCard;
